// RigSentinel Calc — DDR PDF export (jsPDF). Reuses the calc-sheet visual
// language: dark header with amber rule, IBM-Plex-style helvetica, alternating
// section blocks. Multi-page safe.
import jsPDF from "jspdf";
import { buildReport } from "./templates";

const COLORS = {
  amber: [255, 179, 0],
  dark: [10, 11, 14],
  text: [33, 37, 44],
  muted: [104, 113, 120],
  border: [200, 200, 200],
};

function header(doc, title, subtitle) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...COLORS.dark);
  doc.rect(0, 0, w, 22, "F");
  doc.setFillColor(...COLORS.amber);
  doc.rect(0, 22, w, 1.2, "F");
  doc.setTextColor(...COLORS.amber);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("RigSentinel", 12, 11);
  doc.setTextColor(220, 220, 220);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Daily Drilling Report  ·  Draft Documentation Support", 12, 17);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(title, 12, 34);
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.muted);
    doc.text(subtitle, 12, 39);
  }
}

function footer(doc, pageNum, total) {
  const h = doc.internal.pageSize.getHeight();
  const w = doc.internal.pageSize.getWidth();
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.2);
  doc.line(12, h - 14, w - 12, h - 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...COLORS.muted);
  doc.text(
    "RigSentinel · DDR draft documentation support only. Review, correct, and approve under company procedures before submission.",
    12,
    h - 9,
  );
  doc.text(`Page ${pageNum} / ${total}`, w - 12, h - 9, { align: "right" });
}

export function exportDdrPdf(state) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const h = state.header || {};
  const title = `Daily Drilling Report${h.reportNumber ? ` #${h.reportNumber}` : ""}`;
  const subtitle = [h.rigName, h.wellName, h.reportDate].filter(Boolean).join("  ·  ") || "Draft";

  const reportText = (state && state._polishedOverride) ? String(state._polishedOverride) : buildReport(state);
  const lines = reportText.split("\n");

  let page = 1;
  header(doc, title, subtitle);
  let y = 48;
  const pageHeight = doc.internal.pageSize.getHeight();
  const bottom = pageHeight - 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("=== ") && line.endsWith(" ===")) {
      // Section header.
      if (y > bottom - 12) { doc.addPage(); page++; header(doc, title, subtitle); y = 48; }
      const label = line.replace(/^===\s*/, "").replace(/\s*===$/, "");
      doc.setDrawColor(...COLORS.amber);
      doc.setLineWidth(0.5);
      doc.line(12, y, 18, y);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.amber);
      doc.text(label.toUpperCase(), 22, y + 1);
      y += 6;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
      continue;
    }
    const wrapped = doc.splitTextToSize(line || " ", 186);
    for (const w of wrapped) {
      if (y > bottom) { doc.addPage(); page++; header(doc, title, subtitle); y = 48; }
      doc.text(w, 12, y);
      y += 4.6;
    }
  }

  // Disclaimer block at the very end (own page if low).
  if (y > pageHeight - 60) { doc.addPage(); page++; header(doc, title, subtitle); y = 48; }
  doc.setDrawColor(...COLORS.amber);
  doc.setLineWidth(0.4);
  doc.rect(12, y + 2, 186, 36);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.amber);
  doc.text("Disclaimer", 16, y + 9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.text);
  const dis = doc.splitTextToSize(
    "RigSentinel Daily Drilling Report tools provide draft documentation support only. All reports must be reviewed, corrected, and approved by competent rig/site personnel under company procedures, client requirements, and applicable regulations before submission.",
    178,
  );
  let dy = y + 15;
  for (const d of dis) { doc.text(d, 16, dy); dy += 4.4; }
  y += 42;

  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); footer(doc, i, total); }

  const safe = (s) => (s || "").toString().replace(/[^a-z0-9-_]+/gi, "_").slice(0, 40);
  const fname = `RigSentinel_DDR_${safe(h.reportNumber) || "draft"}_${safe(h.wellName) || "well"}_${(h.reportDate || "").replace(/[^0-9]/g, "") || Date.now()}.pdf`;
  doc.save(fname);
}
